import { FadeLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Avatar, Card, Stack, Button, Container, Typography, TablePagination, IconButton } from '@mui/material';
import { ConfirmationDialog } from '../../components/general/ConfirmationDialog';
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { ListToolbar } from '../../components/tables';
import useQuery from '../../hooks/useQuery';
import { useApi } from '../../hooks/useApi';
import AgGridWrapper, { agDefaultColDef } from '../../components/tables/AgGridWrapper';

const BRAND = '#032C65';
const override = { margin: '0 auto' };

export default function Buyer() {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState();
  const [pagination, setPagination] = useState({ total: 0, per_page: 10, current_page: 1, last_page: 1 });
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const { doDelete } = useApi();

  const fetchData = useQuery();
  const { t } = useTranslation();

  const refreshTable = async () => {
    setIsLoading(true);
    await fetchData('users', filter, sort, setUsers, sortField, setPagination, page, () => {});
    setIsLoading(false);
  };

  useEffect(() => {
    const delay = setTimeout(refreshTable, 500);
    return () => clearTimeout(delay);
  }, [filter, sort, page, sortField]);

  const handleDelete = async () => {
    setLoading(true);
    doDelete(`users/${selectedUser.hashid}`, t('delete_success'), t('delete_error'), refreshTable);
    setLoading(false);
    setShowDeleteDialog(false);
  };

  const columnDefs = [
    {
      field: 'name',
      headerName: t('Name'),
      flex: 1,
      minWidth: 180,
      filter: 'agTextColumnFilter',
      cellRenderer: (params) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Avatar alt={params.value} src={params.data.picture} sx={{ width: 32, height: 32 }} />
          <span style={{ fontWeight: 500 }}>{params.value}</span>
        </div>
      ),
    },
    { field: 'email', headerName: t('Email'), flex: 1, minWidth: 180, filter: 'agTextColumnFilter' },
    { field: 'role', headerName: t('Role'), flex: 1, minWidth: 120, filter: 'agTextColumnFilter' },
    {
      headerName: '',
      width: 100,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      cellRenderer: (params) => (
        <div style={{ display: 'flex', gap: 4 }}>
          <IconButton size="small" color="primary" component={RouterLink} to={`/dashboard/user/edit/${params.data.hashid}`}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setSelectedUser(params.data); setShowDeleteDialog(true); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Page title={t('Users')}>
      <Container maxWidth="xl">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>{t('Users')}</Typography>
          <Button
            variant="contained"
            component={RouterLink}
            to="/dashboard/user/save"
            startIcon={<Iconify icon="eva:plus-fill" />}
            sx={{ background: BRAND, '&:hover': { background: '#054a9e' }, borderRadius: 2, px: 3 }}
          >
            {t('New_User')}
          </Button>
        </Stack>
        <Card>
          <ListToolbar numSelected={0} filterName={filter} onFilterName={(e) => setFilter(e.target.value)} placeholder={t('Users')} />
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
              <FadeLoader color="blue" loading cssOverride={override} size={50} />
            </div>
          ) : (
            <AgGridWrapper rowData={users} columnDefs={columnDefs} defaultColDef={agDefaultColDef} clickable />
          )}
          <TablePagination
            component="div"
            rowsPerPageOptions={[]}
            count={pagination.total}
            rowsPerPage={pagination.per_page || 10}
            page={page - 1}
            onPageChange={(e, newPage) => setPage(newPage + 1)}
          />
          <ConfirmationDialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)} onConfirm={handleDelete} message={t('delete_confirm')} />
        </Card>
      </Container>
    </Page>
  );
}
